import { Button, CircularProgress, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip } from '@material-ui/core';
import { DeviceData, bind, loadDeviceBindings, renameBinding, unbind, unbindAllDevices } from '../../modules/bindings';
import { IStoreState, ThunkDispatch } from '../../modules';
import React, { ChangeEvent, Component, FormEvent, Fragment } from 'react';
import { Theme, WithStyles, createStyles, withStyles } from '@material-ui/core/styles';
import { faEllipsisV, faTrashAlt } from '@fortawesome/free-solid-svg-icons';

import BindDevice from '../common/BindDevice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Redirect } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';

const styles = (theme: Theme) => createStyles({
  root: {
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    maxWidth: '45rem',
  },
  progress: {
    margin: '5rem auto',
  },
  heading: {
    marginLeft: theme.spacing(1),
  },
  authHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
  },
}
)

type State = {
  error: string
  loading: boolean
  showBindForm: boolean
  deviceLabel: string
  bindingMenuAnchor: object | null
  bindingMenuOpen: boolean
  bindingMenuDeviceData: DeviceData | null
  renameDeviceData: string | null
}

class BindingsPane extends Component<Props, State> {
  state = {
    error: "",
    loading: true,
    showBindForm: false,
    deviceLabel: "",
    bindingMenuAnchor: null,
    bindingMenuOpen: false,
    bindingMenuDeviceData: null,
    renameDeviceData: null
  }

  componentDidMount() {
    this.loadDeviceBindings()

    document.title = "Device Bindings"
  }

  loadDeviceBindings = () => {
    this.setState({
      loading: true
    })
    return this.props.loadDeviceBindings()
      .catch((error: Error) => {
        console.error(error)
      })
      .finally(() => {
        this.setState({ loading: false })
      })
  }

  handleUnbindAll = () => {
    this.props.unbindAllDevices()
      .catch((error: Error) => {
        this.setState({
          error: error.message
        })
      }
      )
  }

  handleBindingMenuClose = () => {
    this.setState({
      bindingMenuOpen: false
    })
  }

  handleBindingMenuOpen = (deviceData: DeviceData) => (e: React.MouseEvent<HTMLElement>) => {
    this.setState({
      bindingMenuOpen: true,
      bindingMenuAnchor: e.target,
      bindingMenuDeviceData: deviceData
    })
  }

  handleBindingMenuDelete = () => {
    let deviceData = this.state.bindingMenuDeviceData

    if (deviceData !== null)
      this.props.unbind((deviceData as DeviceData).lookupLabel).then(() => {
        this.loadDeviceBindings()
      }).catch((error: Error) => {
        this.setState({
          error: error.message
        })
      })
    this.handleBindingMenuClose()
  }

  handleBindingMenuRename = () => {
    let deviceData = this.state.bindingMenuDeviceData
    this.handleBindingMenuClose()

    if (deviceData !== null) {
      this.setState({
        renameDeviceData: (deviceData as DeviceData).lookupLabel
      })
    }

  }

  handleRenameDeviceDataChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      renameDeviceData: e.target.value
    })
  }

  handleRenameDeviceDataBlur = (lookupLabel: string) => (e: FormEvent<HTMLFormElement> | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (lookupLabel === this.state.renameDeviceData) {
      this.setState({
        renameDeviceData: null
      })
      return
    }

    const { loadDeviceBindings, renameBinding } = this.props

    renameBinding(lookupLabel, this.state.renameDeviceData || "")
      .then((result: any) => {
        if (result)
          loadDeviceBindings()
      })
      .finally(() => {
        this.setState({
          renameDeviceData: null
        })
      })
    try {
      typeof e.preventDefault === "function" && e.preventDefault()
    }
    catch (e) { }
  }

  render = () => {
    const { classes, bindings } = this.props;
    const { loading, bindingMenuDeviceData, renameDeviceData } = this.state

    if (!this.props.auth.authenticated) {
      return <Redirect to="/" />
    }

    return (
      <div className={classNames(classes.root)}>
        {loading ?
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress className={classes.progress} />
          </div>
          : <Fragment>
            <BindDevice complete={() => this.loadDeviceBindings()} />
            {this.props.bindings.boundDevices.length === 0 ?
              <div style={{ textAlign: 'center' }}>
                <Typography>No devices bound</Typography>
              </div>
              :
              <Paper>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Lookup Label</TableCell>
                      <TableCell>Binding Type</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Unbind all devices">
                          <Button onClick={this.handleUnbindAll} disabled={this.props.bindings.boundDevices.length === 0}>
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </Button>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {
                      this.props.bindings.boundDevices.map((deviceData: any) =>
                        <TableRow selected={bindings.deviceBindingLabel === deviceData.lookupLabel} hover={true} key={deviceData.lookupLabel}>
                          <TableCell>
                            {renameDeviceData === null || deviceData !== bindingMenuDeviceData
                              ? <Fragment>
                                {deviceData.lookupLabel}
                                {bindings.deviceBindingLabel === deviceData.lookupLabel
                                  ? <span style={{ fontStyle: 'italic', color: '#999' }}> (this device)</span>
                                  : ""
                                }
                              </Fragment>
                              : <form onSubmit={this.handleRenameDeviceDataBlur(deviceData.lookupLabel)}>
                                <TextField
                                  label="Device Label"
                                  value={renameDeviceData}
                                  onChange={this.handleRenameDeviceDataChange}
                                  onBlur={this.handleRenameDeviceDataBlur(deviceData.lookupLabel)}
                                  inputRef={(input: HTMLInputElement) => input && setTimeout(() => input.focus(), 100)}
                                />
                              </form>
                            }
                          </TableCell>
                          <TableCell>{deviceData.bindingType}</TableCell>
                          <TableCell align="right">
                            <Button onClick={this.handleBindingMenuOpen(deviceData)}>
                              <FontAwesomeIcon icon={faEllipsisV} />
                            </Button></TableCell>
                        </TableRow>
                      )
                    }
                  </TableBody>
                </Table>
                <Menu
                  id="simple-menu"
                  anchorEl={this.state.bindingMenuAnchor}
                  open={this.state.bindingMenuOpen}
                  onClose={this.handleBindingMenuClose}
                >
                  <MenuItem onClick={this.handleBindingMenuRename}>Rename</MenuItem>
                  <MenuItem onClick={this.handleBindingMenuDelete}>Delete</MenuItem>
                </Menu>
              </Paper>
            }
          </Fragment>
        }
      </div>
    )
  }
}

const mapStateToProps = ({ auth, authmethods, intl, bindings }: IStoreState) => ({
  auth,
  authmethods,
  intl,
  bindings
})

const mapDispatchToProps = (dispatch: ThunkDispatch) =>
  bindActionCreators(
    {
      push,
      loadDeviceBindings,
      bind,
      unbind,
      unbindAllDevices,
      renameBinding
    }, dispatch
  )

interface Props extends WithStyles<typeof styles>, ReturnType<typeof mapStateToProps>, ReturnType<typeof mapDispatchToProps> {
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(BindingsPane));